import '@/styles/globals.css'
import { CssBaseline } from '@mui/material'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ThemeProvider } from '@mui/material/styles'
import React, { useEffect } from 'react'
import { AppProps } from 'next/app'
import { hotjar } from 'react-hotjar'
import Head from 'next/head'
import { AuthProvider } from 'context/JWTContext'
import { getTheme } from '@/styles/theme/theme'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import FormModalProvider from 'context/FormModalContext'
import { useRouter } from 'next/router'
import { Toaster } from 'react-hot-toast'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import enGb from 'dayjs/locale/en-gb'
import { applyCountryFlagPolyfill, needsCountryFlagPolyfill } from '@/utils/countryFlagPolyfill'
import { useWalletSocket } from 'hooks/useWalletSocket'
import { useAffiliateRefCapture } from 'hooks/useAffiliateRefCapture'
import { useAttributionCapture } from 'hooks/useAttributionCapture'
import { isTBLPage } from '@/utils/tblUtils'
import { MobileBottomNav } from 'components/shared/MobileBottomNav'

type ComponentWithLayout = AppProps & {
  Component: AppProps['Component'] & {
    PageLayout?: React.ComponentType
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      staleTime: 1000 * 10,
      retry: 1,
    },
  },
})

// Lives inside AuthProvider so the socket closes on logout.
function WalletSocket() {
  useWalletSocket()
  return null
}

function MyApp({ Component, pageProps }: ComponentWithLayout) {
  const router = useRouter()

  const { iframe } = router.query
  const isLandingPage = router.asPath.includes('landing')
  const showBottomNav =
    isTBLPage(pageProps.page) && !iframe && !router.pathname.startsWith('/[slug]/auth')

  useEffect(() => {
    if (pageProps.page?.hotjar_id && pageProps.page?.hotjar_sv)
      hotjar.initialize(pageProps.page.hotjar_id, pageProps.page.hotjar_sv)
  }, [pageProps.page?.hotjar_id, pageProps.page?.hotjar_sv])
  useEffect(() => {
    if (needsCountryFlagPolyfill()) return applyCountryFlagPolyfill()
    return undefined
  }, [])
  const theme = getTheme(pageProps, isLandingPage)
  useAffiliateRefCapture(isTBLPage(pageProps.page))
  useAttributionCapture(isTBLPage(pageProps.page))

  return (
    <>
      <Head key="main">
        <title>{pageProps.page?.site_title}</title>
        <meta
          name="keywords"
          content="join, game, predictor, play, fantasy, open, championship, golf, free"
        />

        <meta name="twitter:image" content={pageProps.page?.metadata_image} />
        {/* <meta property="og:title" content={pageProps.page?.metadata_content} /> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageProps.page?.metadata_content} />
        {pageProps.page?.metadata_image && (
          <meta property="og:image" content={pageProps.page?.metadata_image} />
        )}

        <meta name="og:url" content={pageProps.page?.company_domain} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:type" content="website" />
        <meta property="og:image:type" content="image/jpg" />
        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="300" />
        <link rel="icon" href={pageProps.page?.favicon} type="image/png" />
        <link rel="icon" type="image/x-icon" href={pageProps.page?.favicon} />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href={pageProps.page?.favicon}
          type="image/png"
        />
        <link rel="mask-icon" href={pageProps?.page?.favicon} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={pageProps.page?.favicon}
          type="image/png"
        />
        <link
          rel="apple-touch-icon"
          sizes="16x16"
          href={pageProps.page?.favicon}
          type="image/png"
        />
        <link
          rel="apple-touch-icon"
          sizes="32x32"
          href={pageProps.page?.favicon}
          type="image/png"
        />
        <link rel="shortcut icon" href={pageProps.page?.favicon} type="image/png" />
      </Head>
      <Toaster />
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider adapterLocale={enGb.name} dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <WalletSocket />
            <ThemeProvider theme={theme}>
              <FormModalProvider page={pageProps.page}>
                <CssBaseline />
                {Component.PageLayout && !iframe ? (
                  // @ts-ignore
                  <Component.PageLayout
                    page={pageProps.page}
                    leagues={pageProps.myLeagues}
                    siteContent={pageProps.siteContent}
                    shareMeta={pageProps.shareMeta}
                  >
                    <Component key={router.asPath} {...pageProps} page={pageProps?.page} />
                  </Component.PageLayout>
                ) : (
                  <Component {...pageProps} page={pageProps?.page} />
                )}
                {showBottomNav && (
                  <MobileBottomNav
                    page={pageProps.page}
                    initialRaffles={
                      router.pathname === '/[slug]/prize-draw' ? pageProps.raffles : undefined
                    }
                  />
                )}
              </FormModalProvider>
            </ThemeProvider>
          </AuthProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </>
  )
}

export default MyApp
